import type { WooCommerceConfig } from './woocommerce';

/**
 * LojaPilot store connection model.
 *
 * Every store managed by the app has a connection type. Existing
 * configurations predate this field, so `undefined` always means
 * the legacy WooCommerce REST API path — never require the field.
 */
export type StoreConnectionType = 'woocommerce_api' | 'woocommerce_plugin' | 'native';

export type ConnectionStatus = 'pending' | 'active' | 'degraded' | 'disconnected' | 'error';

export interface StoreConnectionMeta {
  connectionType?: StoreConnectionType;
  /** UUID of the row in the `stores` table, when the store was provisioned server-side. */
  nativeStoreId?: string;
  connectionStatus?: ConnectionStatus;
  externalRef?: string;
  onboardingCompleted?: boolean;
  pluginVersion?: string;
  pairedAt?: string;
}

export const getConnectionType = (config: Partial<WooCommerceConfig & StoreConnectionMeta> | null | undefined): StoreConnectionType => {
  if (!config) return 'woocommerce_api';
  if (config.connectionType === 'native' || config.connectionType === 'woocommerce_plugin') return config.connectionType;
  return 'woocommerce_api';
};

export const isNativeStore = (config: Partial<WooCommerceConfig & StoreConnectionMeta> | null | undefined): boolean =>
  getConnectionType(config) === 'native';

/**
 * Feature availability per connection type. The UI uses this to hide or
 * disable features a store type cannot support, instead of letting calls
 * fail mysteriously. 'full' | 'read' | 'partial' | false.
 */
export type Capability = 'full' | 'read' | 'partial' | false;

export interface StoreCapabilities {
  products: Capability;
  productVariations: Capability;
  categories: Capability;
  brands: Capability;
  orders: Capability;
  customers: Capability;
  stockManagement: Capability;
  mediaUpload: Capability;
  loyalty: Capability;
  pos: Capability;
  reports: Capability;
  webhooks: Capability;
  storeApiClientApp: Capability;
  pushNotifications: Capability;
  csvImport: Capability;
}

const WOOCOMMERCE_CAPABILITIES: StoreCapabilities = {
  products: 'full',
  productVariations: 'full',
  categories: 'full',
  brands: 'full',
  orders: 'full',
  customers: 'full',
  stockManagement: 'full',
  mediaUpload: 'full',
  loyalty: 'full',
  pos: 'full',
  reports: 'full',
  webhooks: 'full',
  storeApiClientApp: 'full',
  pushNotifications: 'full',
  csvImport: 'partial', // via Woo products endpoint
};

const NATIVE_CAPABILITIES: StoreCapabilities = {
  products: 'full',
  productVariations: 'partial', // stored, no Woo variation engine
  categories: 'full',
  brands: false,
  orders: 'full',
  customers: 'partial', // created implicitly from orders
  stockManagement: 'full',
  mediaUpload: 'partial', // image URLs only
  loyalty: false,
  pos: 'partial',
  reports: 'partial',
  webhooks: false,
  storeApiClientApp: 'partial', // native catalog browse only
  pushNotifications: 'partial',
  csvImport: 'full',
};

export const getStoreCapabilities = (config: Partial<WooCommerceConfig & StoreConnectionMeta> | null | undefined): StoreCapabilities =>
  isNativeStore(config) ? NATIVE_CAPABILITIES : WOOCOMMERCE_CAPABILITIES;

export const capabilityAvailable = (cap: Capability) => cap !== false;

export const CONNECTION_TYPE_LABELS: Record<StoreConnectionType, string> = {
  woocommerce_api: 'WooCommerce (API)',
  woocommerce_plugin: 'WooCommerce (Plugin LojaPilot)',
  native: 'LojaPilot',
};

export const CONNECTION_STATUS_LABELS: Record<ConnectionStatus, string> = {
  pending: 'Pendente',
  active: 'Ativa',
  degraded: 'Instável',
  disconnected: 'Desligada',
  error: 'Erro',
};
